import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { InviteDto, MyInvitesDto } from '@/Domain/api/AI/dto/Invite.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import { QuickchatAiIcon } from '@/Domain/components/icons/QuickchatAiWordmark.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

// Discriminates these events from the identical pair fired by AiInvitation.
const INVITATION_SOURCE = 'landing_page';

const goToScenario = (scenarioId: string): void => {
  // Full reload so the JWT carries the new scenario role.
  window.location.assign(`/i/${scenarioId}/`);
};

export function AcceptInvitationPage() {
  const { t } = useTranslation('accept_invitation');
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [busyToken, setBusyToken] = useState<string | null>(null);

  const invitesQuery = useQuery<MyInvitesDto>({
    queryKey: [QueryKey.invites.mine],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const acceptMutation = useMutation<unknown, unknown, string>({
    mutationKey: [QueryKey.invites.accept],
  });

  const declineMutation = useMutation<unknown, unknown, string>({
    mutationKey: [QueryKey.invites.decline],
  });

  const ignoreMutation = useMutation<{ scenario_id: string }, unknown, void>({
    mutationKey: [QueryKey.invites.ignore],
  });

  const invites = invitesQuery.data?.invitations ?? [];
  const hasExistingAgents = invitesQuery.data?.has_existing_implementations ?? false;

  const hasFiredViewedRef = useRef(false);
  useEffect(() => {
    // Documented as "rendered a non-empty invite list", so an empty payload — or an
    // old pod's bare list, which reads as empty — must not fire it.
    if (hasFiredViewedRef.current || invitesQuery.isLoading || invites.length === 0) return;
    hasFiredViewedRef.current = true;
    capture(PostHogEventEnum.InvitationLandingViewed, {
      pending_invitation_count: invites.length,
    });
  }, [invitesQuery.isLoading, invitesQuery.data, capture]);

  // If we landed here with no pending invites (e.g. they were all accepted
  // in another tab between the redirect and this query firing, or the last one
  // was just declined), bounce to the root. Done in an effect — not during
  // render — so StrictMode's double-invoke doesn't fire window.location.assign
  // twice and so the navigation doesn't happen mid-render.
  // `invites`, not `data.invitations`: an old pod's bare list throws here, and this route has no error boundary.
  useEffect(() => {
    if (invitesQuery.isLoading || !invitesQuery.data) return;
    if (invites.length === 0) {
      window.location.assign('/');
    }
  }, [invitesQuery.isLoading, invitesQuery.data]);

  const isBusy = busyToken !== null || ignoreMutation.isPending;

  const handleAccept = async (invite: InviteDto): Promise<void> => {
    if (isBusy) return;
    setBusyToken(invite.token);
    try {
      await acceptMutation.mutateAsync(invite.token);
      capture(PostHogEventEnum.InvitationAccepted, {
        scenario_id: invite.scenario_id,
        role: invite.role,
        pending_invitation_count: invites.length,
        has_inviter_email: invite.inviter_email != null,
        source: INVITATION_SOURCE,
      });
      goToScenario(invite.scenario_id);
    } catch (error) {
      console.error('Error accepting invite:', error);
      toast.error(t('action_failed'));
      setBusyToken(null);
    }
  };

  const handleDecline = async (invite: InviteDto): Promise<void> => {
    if (isBusy) return;
    setBusyToken(invite.token);
    try {
      await declineMutation.mutateAsync(invite.token);
      capture(PostHogEventEnum.InvitationDeclined, {
        scenario_id: invite.scenario_id,
        role: invite.role,
        remaining_invitation_count: invites.length - 1,
        source: INVITATION_SOURCE,
      });
      // Cancel first: a /my_invites refetch already in flight would otherwise land
      // after this write and put the declined row back.
      await queryClient.cancelQueries({ queryKey: [QueryKey.invites.mine] });
      queryClient.setQueryData<MyInvitesDto>(
        [QueryKey.invites.mine],
        (previous) =>
          previous && {
            ...previous,
            invitations: previous.invitations.filter((item) => item.token !== invite.token),
          },
      );
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast.error(t('action_failed'));
    } finally {
      setBusyToken(null);
    }
  };

  const handleIgnore = async (): Promise<void> => {
    if (isBusy) return;
    try {
      const data = await ignoreMutation.mutateAsync();
      capture(PostHogEventEnum.InvitationIgnored, {
        scenario_id: data.scenario_id,
        pending_invitation_count: invites.length,
        // The copy and the outcome differ: "Skip" reopens an existing agent, "Ignore" mints one.
        reused_existing_implementation: hasExistingAgents,
      });
      goToScenario(data.scenario_id);
    } catch (error) {
      console.error('Error ignoring invitation:', error);
      toast.error(t('action_failed'));
    }
  };

  if (invitesQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  // "/" now bounces here for as long as an invite is pending, so a failed
  // /my_invites must offer a way out: falling through to the empty state below
  // would send the user to "/", which sends them straight back with no content.
  // Gated on having nothing to render, so a failed background refetch never
  // replaces a list the user is already looking at.
  if (invitesQuery.isError && !invitesQuery.data) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-foreground"
        data-testid="accept-invitation-error"
      >
        <p className="text-muted-foreground">{t('load_failed')}</p>
        <Button variant="dark" onClick={() => void invitesQuery.refetch()}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  if (invites.length === 0) {
    // The empty-state useEffect above has already scheduled a redirect to /.
    return null;
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-background p-6 text-foreground">
      <QuickchatAiIcon
        className="absolute top-6 left-6 scale-[1.2] text-foreground"
        data-testid="accept-invitation-page-logo"
      />

      <div className="w-full max-w-xl space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {invites.length === 1 ? t('subtitle_singular') : t('subtitle_plural')}
          </p>
        </div>

        <div className="space-y-3" data-testid="accept-invitation-list">
          {invites.map((invite) => {
            const inviterLabel = invite.inviter_email ?? t('inviter_fallback');
            const agentName = invite.one_word_description || t('untitled_agent');
            return (
              <Card key={invite.token} data-testid={`invite-card-${invite.token}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <UserPlus className="size-4 text-muted-foreground" />
                    {agentName}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <Mail className="size-4 shrink-0" />
                    <span>
                      {t('invited_by', { inviter: inviterLabel })}
                      <span className="ml-2 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        {invite.role}
                      </span>
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex items-center gap-2">
                  {/* Disabled per card, not globally: dimming every row reads as "all of them are being accepted". */}
                  <Button
                    variant="dark"
                    className="flex-1"
                    loading={busyToken === invite.token && acceptMutation.isPending}
                    disabled={busyToken === invite.token}
                    onClick={() => void handleAccept(invite)}
                    data-testid={`accept-invite-${invite.token}`}
                  >
                    {busyToken === invite.token && acceptMutation.isPending
                      ? t('accepting')
                      : t('accept')}
                  </Button>
                  <Button
                    variant="ghost"
                    loading={busyToken === invite.token && declineMutation.isPending}
                    disabled={busyToken === invite.token}
                    onClick={() => void handleDecline(invite)}
                    data-testid={`decline-invite-${invite.token}`}
                  >
                    {busyToken === invite.token && declineMutation.isPending
                      ? t('declining')
                      : t('decline')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex flex-col items-center gap-2 pt-4">
          <Button
            variant="ghost"
            disabled={isBusy}
            onClick={() => void handleIgnore()}
            data-testid="ignore-invitations"
          >
            {ignoreMutation.isPending
              ? hasExistingAgents
                ? t('skipping')
                : t('ignoring')
              : hasExistingAgents
                ? t('skip')
                : t('ignore')}
          </Button>
          {/* Only the "we'll mint you an agent" branch needs explaining; "Skip for now" speaks for itself. */}
          {!hasExistingAgents && (
            <p className="text-center text-xs text-muted-foreground">{t('ignore_hint')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
